/**
 * St John Variant Info
 * Shows a short explainer for the option value that is currently selected, with a
 * tooltip arrow aimed at the selected pill so the panel reads as part of the picker.
 *
 * One element is rendered per option value, and each one decides on its own whether it
 * should be visible. That keeps the blocks independent, so a merchant can add, remove or
 * reorder them in the theme editor without any shared controller to keep in sync.
 *
 * Input attributes:
 * - data-section-id: owning section, so variant changes from other sections are ignored
 * - data-option-position: 1-based index of the product option this panel describes
 * - data-option-value: the option value this panel belongs to
 */
if (!customElements.get("stjohnwa-variant-info")) {
  class StjohnwaVariantInfo extends HTMLElement {
    connectedCallback() {
      this.optionPosition = parseInt(this.dataset.optionPosition, 10) || 1;
      this.optionValue = this.normalise(this.dataset.optionValue);

      this.unsubscribe = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        this.onVariantChange.bind(this),
      );

      // The pills wrap at narrow widths, so the arrow has to be re-aimed on resize.
      this.onResize = FoxTheme.utils.throttle(this.positionArrow.bind(this));
      window.addEventListener("resize", this.onResize);

      this.positionArrow();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      window.removeEventListener("resize", this.onResize);
    }

    normalise(value) {
      return typeof value === "string" ? value.trim().toLowerCase() : "";
    }

    onVariantChange(event) {
      const data = event && event.data;
      if (!data || data.sectionId !== this.dataset.sectionId) return;
      this.refreshState(data.variant);
    }

    // Visible only while this element's option value is the selected one.
    refreshState(variant) {
      if (!variant) {
        this.hidden = true;
        return;
      }

      const selected = this.normalise(variant["option" + this.optionPosition]);
      this.hidden = selected !== this.optionValue;
      if (!this.hidden) this.positionArrow();
    }

    // The fieldset for the option this panel describes.
    get optionWrapper() {
      const selects = document.getElementById(`variant-selects-${this.dataset.sectionId}`);
      if (!selects) return null;
      return selects.querySelectorAll(".product-form__input")[this.optionPosition - 1] || null;
    }

    positionArrow() {
      // Measuring a hidden element returns zeroes, so wait until it is shown.
      if (this.hidden) return;

      const wrapper = this.optionWrapper;
      const pill = wrapper && wrapper.querySelector('input[type="radio"]:checked + label');
      if (!pill) {
        this.dataset.arrow = "false";
        return;
      }

      const pillRect = pill.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();

      // A pill on an earlier row would leave the arrow pointing at open space.
      if (wrapperRect.bottom - pillRect.bottom > 4) {
        this.dataset.arrow = "false";
        return;
      }

      const selfRect = this.getBoundingClientRect();
      const inset = 2.4 * parseFloat(getComputedStyle(document.documentElement).fontSize || 10);
      const centre = pillRect.left + pillRect.width / 2 - selfRect.left;
      const clamped = Math.min(Math.max(centre, inset), selfRect.width - inset);

      this.style.setProperty("--arrow-x", `${clamped}px`);
      this.dataset.arrow = "true";
    }
  }
  customElements.define("stjohnwa-variant-info", StjohnwaVariantInfo);
}
